import { DataApiResponse } from '@digilize/shared/definitions/src';
import { Observable } from 'rxjs';

/**
 * PAUL-3584. Paging state for one infinite-scroll "connected items" list.
 *
 * A show type's list of upcoming screenings runs to four figures at a busy cinema, so all three
 * lists page the same way the shows screen does: 40 rows per fetch, `start` = rows already held,
 * and the server filters the search. Holding the state here keeps the three lists identical
 * instead of three slightly different copies of the same twenty lines.
 */
export class ConnectedListState<T> {
  static readonly PAGE_SIZE = 40;

  items: T[] = [];
  total: number | undefined;
  loading = false;
  search = '';

  constructor(
    private readonly fetch: (params: Record<string, unknown>) => Observable<DataApiResponse<T[]>>,
    private readonly onError?: (error: unknown) => void
  ) {}

  get canLoadMore(): boolean {
    return this.total === undefined || this.items.length < this.total;
  }

  get isEmpty(): boolean {
    return !this.loading && this.items.length === 0;
  }

  /** Re-runs the list from the first page - use after a search change or a detach. */
  reset(): void {
    this.items = [];
    this.total = undefined;
  }

  loadMore(): void {
    if (this.loading || !this.canLoadMore) {
      return;
    }

    this.loading = true;

    const start = this.items.length;

    this.fetch({
      start,
      length: ConnectedListState.PAGE_SIZE,
      'search.value': this.search,
    }).subscribe({
      next: (response) => {
        // A reset while this page was in flight makes the response stale: appending it would
        // duplicate rows or resurrect ones that were just detached.
        if (start !== 0 && start !== this.items.length) {
          this.loading = false;
          return;
        }

        this.items = start === 0 ? response.data ?? [] : this.items.concat(response.data ?? []);
        this.total = response.records_total;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.onError?.(error);
      },
    });
  }
}
